import { useEffect, useMemo, useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { CreditCard, LogOut, Mail, ShieldCheck, User2 } from "lucide-react";
import RankBadge from "../../components/club/RankBadge";
import { ClubApi } from "../../services/clubApi";
import { devError, devLog } from "../../utils/devLog";

const RANK_LABELS: Record<string, string> = {
  junior_detective: "Junior Detective",
  detective: "Detective",
  senior_detective: "Senior Detective",
  lead_detective: "Lead Detective",
  special_investigator: "Special Investigator",
};

const formatDate = (value: unknown) => {
  const parsed = new Date(String(value || ""));
  if (Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatStatusLabel = (value: unknown) => {
  const normalized = String(value || "active")
    .trim()
    .toLowerCase();
  if (!normalized) return "Active";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const ClubSettingsPage = () => {
  const { user } = useUser();
  const clerk = useClerk();
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState("");
  const [member, setMember] = useState<any>(null);
  const [rankProgress, setRankProgress] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await ClubApi.getMe();
        setMember(response.member || null);
        setRankProgress(response.rankProgress || null);
        devLog("auth", "agency_settings_loaded", {
          memberStatus: response?.member?.status || "unknown",
          paidMonthsCount: Number(response?.member?.paidMonthsCount || 0),
          successfulSolvesCount: Number(response?.member?.successfulSolvesCount || 0),
          hasMembershipNumber: Boolean(response?.member?.membershipNumber),
          fulfillmentMode: response?.member?.fulfillmentMode || "digital",
        });
      } catch (err: any) {
        devError("auth", "agency_settings_load_failed", {
          status: err?.response?.status,
          message: err?.response?.data?.error || err?.message || "Failed to load settings",
        });
        setError(err?.response?.data?.error || "Failed to load account details");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const onSignOut = async () => {
    try {
      setSigningOut(true);
      devLog("auth", "agency_settings_sign_out_requested", {
        userId: user?.id || "unknown",
      });
      await clerk.signOut({ redirectUrl: "/agency" });
    } catch (err: any) {
      devError("auth", "agency_settings_sign_out_failed", {
        message: err?.message || "Sign out failed",
      });
      setError("Failed to sign out. Please try again.");
      setSigningOut(false);
    }
  };

  const summaryCards = useMemo(
    () => [
      {
        label: "Subscription",
        value: "Digital Membership",
        detail: "Physical fulfillment has been removed.",
      },
      {
        label: "Status",
        value: formatStatusLabel(member?.status),
        detail: `Rank: ${String(rankProgress?.currentRankLabel || "").trim() || RANK_LABELS[String(member?.rank || "")] || "Detective"}`,
      },
      {
        label: "Successful Solves",
        value: String(Number(rankProgress?.successfulSolvesCount || member?.successfulSolvesCount || 0)),
        detail: rankProgress?.nextRankLabel
          ? `${Number(rankProgress?.solvesToNextRank || 0)} until ${String(rankProgress?.nextRankLabel || "")}`
          : "Top rank reached",
      },
      {
        label: "Member Since",
        value: formatDate(member?.createdAt),
        detail: `Last updated ${formatDate(member?.updatedAt)}`,
      },
    ],
    [member?.createdAt, member?.paidMonthsCount, member?.rank, member?.status, member?.updatedAt, rankProgress?.currentRankLabel, rankProgress?.nextRankLabel, rankProgress?.solvesToNextRank, rankProgress?.successfulSolvesCount]
  );

  if (loading) {
    return <div className="px-4 py-5 text-white sm:px-5 sm:py-6">Loading account details...</div>;
  }

  if (error && !member) {
    return <div className="p-6 text-red-300">{error}</div>;
  }

  return (
    <section className="mx-auto max-w-6xl px-4 py-6 text-[#eceae4] sm:px-5 sm:py-8">
      <div className="rounded-[28px] border border-[#1d2431] bg-[radial-gradient(circle_at_top_right,rgba(66,85,126,0.16),transparent_28%),#080c14] p-6 sm:p-8">
        <div className="flex flex-col gap-5 border-b border-white/8 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c6b894]">Agency Account</p>
            <h1 className="mt-3 text-3xl font-black leading-tight text-white sm:text-4xl">
              Subscription and access record
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-[#aba69a] sm:text-base">
              This account is digital-only. Review your membership status, identity details, and
              access record here.
            </p>
          </div>

          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#394253] bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <LogOut className="h-4 w-4" />
            {signingOut ? "Signing Out..." : "Log Out"}
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl border border-amber-300/25 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
            {error}
          </div>
        ) : null}

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => (
            <article
              key={card.label}
              className="rounded-2xl border border-[#232a37] bg-[#0e1420]/70 p-4"
            >
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#9f9a8e]">{card.label}</p>
              <p className="mt-3 text-2xl font-semibold text-white">{card.value}</p>
              <p className="mt-2 text-sm text-[#9fa6b5]">{card.detail}</p>
            </article>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <article className="rounded-3xl border border-[#202632] bg-[#0a101a] p-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-400/10 text-cyan-100">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#c6b894]">Membership Record</p>
                <h2 className="mt-1 text-xl font-bold text-white">Agency subscription details</h2>
              </div>
            </div>

            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <dt className="text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">Membership Number</dt>
                <dd className="mt-2 text-lg font-semibold text-white">
                  {String(member?.membershipNumber || "Pending Assignment")}
                </dd>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <dt className="text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">Delivery Mode</dt>
                <dd className="mt-2 text-lg font-semibold text-white">Digital</dd>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <dt className="text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">Current Rank</dt>
                <dd className="mt-2 flex items-center gap-3 text-lg font-semibold text-white">
                  <RankBadge
                    rankKey={String(rankProgress?.currentRankKey || member?.rank || "")}
                    rankLabel={String(rankProgress?.currentRankLabel || "").trim() || RANK_LABELS[String(member?.rank || "")] || "Detective"}
                    size={48}
                    className="h-12 w-12 rounded-2xl border border-white/8 bg-white/5 p-1"
                  />
                  <span>
                    {String(rankProgress?.currentRankLabel || "").trim() || RANK_LABELS[String(member?.rank || "")] || "Detective"}
                  </span>
                </dd>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <dt className="text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">Current Status</dt>
                <dd className="mt-2 text-lg font-semibold capitalize text-white">
                  {formatStatusLabel(member?.status)}
                </dd>
              </div>
            </dl>
          </article>

          <article className="rounded-3xl border border-[#202632] bg-[#0a101a] p-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-300/25 bg-amber-300/10 text-amber-100">
                <User2 className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#c6b894]">Identity</p>
                <h2 className="mt-1 text-xl font-bold text-white">Primary account holder</h2>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">Name</p>
                <p className="mt-2 text-lg font-semibold text-white">
                  {String(
                    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                      member?.displayName ||
                      "Not available"
                  )}
                </p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </div>
                <p className="mt-2 text-lg font-semibold text-white">
                  {user?.primaryEmailAddress?.emailAddress || member?.email || "Not available"}
                </p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#9f9a8e]">
                  <CreditCard className="h-3.5 w-3.5" />
                  Subscription Note
                </div>
                <p className="mt-2 text-sm leading-6 text-[#c7c3b8]">
                  Your membership is digital-only. Case files, archive episodes, and briefings
                  are delivered through this agency account.
                </p>
              </div>
            </div>
          </article>
        </div>

        <div className="mt-6 rounded-3xl border border-[#202632] bg-[#0a101a] p-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#c6b894]">Quick Access</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/agency/dashboard"
              className="rounded-full border border-[#394253] bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Back to Dashboard
            </Link>
            <Link
              to="/agency/sessions/manage"
              className="rounded-full border border-[#394253] bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Manage Sessions
            </Link>
            {member?.isAdmin ? (
              <Link
                to="/agency/admin/archive"
                className="rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-5 py-3 text-sm font-semibold text-[#ece1c8] transition hover:brightness-110"
              >
                Archive Import
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClubSettingsPage;
